import { useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Controlled address input with Google Places (New) autocomplete.
 * onChange(text) fires on typing. onSelect({ formatted, street, city, zip }) fires on pick.
 */
export const AddressAutocomplete = ({ value, onChange, onSelect, testId = "address", placeholder = "123 Main St", className = "" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);

  const fetchSuggestions = async (input) => {
    if (!KEY || input.trim().length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Goog-Api-Key": KEY },
        body: JSON.stringify({ input, includedRegionCodes: ["us"], includedPrimaryTypes: ["street_address", "premise", "subpremise"] }),
      });
      const data = await res.json();
      setSuggestions((data.suggestions || []).map((s) => s.placePrediction).filter(Boolean).slice(0, 5));
    } catch { setSuggestions([]); }
  };

  const handleChange = (v) => {
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 300);
  };

  const pick = async (pred) => {
    setSuggestions([]);
    const fallback = pred.structuredFormat?.mainText?.text || pred.text?.text || "";
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${pred.placeId}?languageCode=en`, {
        headers: { "X-Goog-Api-Key": KEY, "X-Goog-FieldMask": "addressComponents,formattedAddress" },
      });
      const place = await res.json();
      const get = (t) => place.addressComponents?.find((c) => c.types.includes(t));
      const streetNo = get("street_number")?.longText || "";
      const route = get("route")?.longText || "";
      const street = `${streetNo} ${route}`.trim() || fallback;
      const city = get("locality")?.longText || get("sublocality")?.longText || get("postal_town")?.longText || "";
      const zip = get("postal_code")?.longText || "";
      onSelect({ formatted: place.formattedAddress || street, street, city, zip });
    } catch {
      onSelect({ formatted: fallback, street: fallback, city: "", zip: "" });
    }
  };

  return (
    <div className="relative">
      <Input data-testid={testId} autoComplete="off" value={value} placeholder={placeholder} className={className}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={() => setTimeout(() => setSuggestions([]), 150)} />
      {suggestions.length > 0 && (
        <ul data-testid={`${testId}-suggestions`} className="absolute z-50 left-0 right-0 mt-1 bg-white border border-ink/20 shadow-lg max-h-64 overflow-auto">
          {suggestions.map((p) => (
            <li key={p.placeId}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); pick(p); }}
                className="w-full text-left px-3 py-2.5 text-sm text-ink hover:bg-ink hover:text-paper transition-colors flex items-start gap-2">
                <MapPin size={14} className="text-rust mt-0.5 shrink-0" />
                <span>{p.text?.text || p.structuredFormat?.mainText?.text}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
